import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './lms-monitoring.css';

const LMSMonitoring = ({ connectorId = null, selectedModels = [], refreshInterval = 60000 }) => {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [usageData, setUsageData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeframe, setTimeframe] = useState('week');

  // Sample colors for charts
  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  
  // Demo usage data
  const demoUsageData = {
    overview: {
      totalSessions: 1248,
      activeUsers: 78,
      questionsAnswered: 3567,
      avgSessionDuration: 14.3, // in minutes
      successRate: 92.4,
      deploymentAge: 18 // in days
    },
    dailyActivity: [
      {day: 'Mon', sessions: 210, users: 45, questions: 602},
      {day: 'Tue', sessions: 180, users: 39, questions: 523},
      {day: 'Wed', sessions: 250, users: 52, questions: 718},
      {day: 'Thu', sessions: 190, users: 43, questions: 535},
      {day: 'Fri', sessions: 220, users: 48, questions: 612},
      {day: 'Sat', sessions: 90, users: 22, questions: 245},
      {day: 'Sun', sessions: 108, users: 27, questions: 332}
    ],
    learningStyles: [
      {name: 'Visual', value: 42},
      {name: 'Auditory', value: 18},
      {name: 'Kinesthetic', value: 14},
      {name: 'Reading/Writing', value: 26}
    ],
    performanceByStyle: [
      {name: 'Visual', correct: 92, engagement: 88},
      {name: 'Auditory', correct: 87, engagement: 82},
      {name: 'Kinesthetic', correct: 84, engagement: 91},
      {name: 'Reading/Writing', correct: 90, engagement: 85}
    ],
    topics: [
      {name: 'Equations', count: 952},
      {name: 'Inequalities', count: 621},
      {name: 'Functions', count: 743},
      {name: 'Polynomials', count: 489},
      {name: 'Word Problems', count: 762}
    ],
    studentFeedback: {
      positive: 72,
      neutral: 21,
      negative: 7,
      recentFeedback: [
        {type: 'positive', text: "The AI explained quadratic equations in a way I finally understood!"},
        {type: 'positive', text: "I love the visual examples, they really help me see the concepts."},
        {type: 'neutral', text: "It's pretty good but I wish it would give more examples."},
        {type: 'negative', text: "The system didn't understand my question about derivatives."}
      ]
    }
  };

  // Fetch models when connectorId changes
  useEffect(() => {
    if (!connectorId) {
      // Use provided models if connector is not available
      if (selectedModels && selectedModels.length > 0) {
        setModels(selectedModels);
        setSelectedModel(selectedModels[0]);
        setLoading(false);
      }
      return;
    }
    
    // In a real implementation, fetch models from the API
    const fetchModels = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/lms/models?connector_id=${connectorId}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch models: ${response.statusText}`);
        }
        
        const data = await response.json();
        setModels(data.models || []);
        
        if (data.models && data.models.length > 0) {
          setSelectedModel(data.models[0]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching models:", err);
        setError(err.message);
        setLoading(false);
        
        // Set demo models for UI testing
        const demoModels = [
          {
            id: "model_101",
            name: "Algebra Assistant",
            model_type: "neo",
            specialty: "algebra",
            status: "active",
            deployed_to: ["course_101"]
          },
          {
            id: "model_102",
            name: "Geometry Helper",
            model_type: "grpo",
            specialty: "geometry",
            status: "active",
            deployed_to: ["course_102"]
          }
        ];
        
        setModels(demoModels);
        setSelectedModel(demoModels[0]);
      }
    };
    
    fetchModels();
  }, [connectorId, selectedModels]);
  
  // Fetch usage data when selectedModel changes or on refresh interval
  useEffect(() => {
    if (!selectedModel) return;
    
    const fetchUsageData = async () => {
      // In a real implementation, fetch usage data from the API
      try {
        // Use the demo data for now
        setUsageData(demoUsageData);
      } catch (err) {
        console.error("Error fetching usage data:", err);
        setError(err.message);
      }
    };
    
    fetchUsageData();
    
    // Set up refresh interval
    const intervalId = setInterval(fetchUsageData, refreshInterval);
    
    return () => clearInterval(intervalId);
  }, [selectedModel, refreshInterval]);

  if (loading) {
    return <div className="lms-monitoring-loading">Loading monitoring data...</div>;
  }
  
  if (error) {
    return <div className="lms-monitoring-error">Error: {error}</div>;
  }
  
  if (!selectedModel) {
    return <div className="lms-monitoring-empty">No models available for monitoring.</div>;
  }
  
  const handleModelChange = (e) => {
    const modelId = e.target.value;
    const model = models.find(m => m.id === modelId);
    setSelectedModel(model);
  };
  
  const handleTimeframeChange = (e) => {
    setTimeframe(e.target.value);
  };
  
  return (
    <div className="lms-monitoring-container">
      <div className="lms-monitoring-header">
        <div className="model-selector">
          <label htmlFor="model-select">Model:</label>
          <select 
            id="model-select" 
            value={selectedModel?.id} 
            onChange={handleModelChange}
            className="model-select-dropdown"
          >
            {models.map(model => (
              <option key={model.id} value={model.id}>
                {model.name} ({model.specialty})
              </option>
            ))}
          </select>
        </div>
        
        <div className="timeframe-selector">
          <label htmlFor="timeframe-select">Timeframe:</label>
          <select 
            id="timeframe-select" 
            value={timeframe} 
            onChange={handleTimeframeChange}
            className="timeframe-select-dropdown"
          >
            <option value="day">Last 24 Hours</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>
      
      <div className="lms-monitoring-tabs">
        <button 
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={activeTab === 'activity' ? 'active' : ''}
          onClick={() => setActiveTab('activity')}
        >
          Activity
        </button>
        <button 
          className={activeTab === 'learning-styles' ? 'active' : ''}
          onClick={() => setActiveTab('learning-styles')}
        >
          Learning Styles
        </button>
        <button 
          className={activeTab === 'content' ? 'active' : ''}
          onClick={() => setActiveTab('content')}
        >
          Content
        </button>
        <button 
          className={activeTab === 'feedback' ? 'active' : ''}
          onClick={() => setActiveTab('feedback')}
        >
          Feedback
        </button>
      </div>
      
      <div className="lms-monitoring-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Sessions</h3>
                <div className="stat-value">{usageData.overview?.totalSessions}</div>
              </div>
              <div className="stat-card">
                <h3>Active Users</h3>
                <div className="stat-value">{usageData.overview?.activeUsers}</div>
              </div>
              <div className="stat-card">
                <h3>Questions Answered</h3>
                <div className="stat-value">{usageData.overview?.questionsAnswered}</div>
              </div>
              <div className="stat-card">
                <h3>Avg. Session Duration</h3>
                <div className="stat-value">{usageData.overview?.avgSessionDuration} min</div>
              </div>
              <div className="stat-card">
                <h3>Success Rate</h3>
                <div className="stat-value">{usageData.overview?.successRate}%</div>
              </div>
              <div className="stat-card">
                <h3>Deployment Age</h3>
                <div className="stat-value">{usageData.overview?.deploymentAge} days</div>
              </div>
            </div>
            
            <div className="chart-container">
              <h3>Daily Activity Overview</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={usageData.dailyActivity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="sessions" stroke="#4F46E5" name="Sessions" />
                  <Line type="monotone" dataKey="users" stroke="#10B981" name="Users" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        
        {activeTab === 'activity' && (
          <div className="activity-tab">
            <div className="chart-row">
              <div className="chart-container">
                <h3>Daily Sessions</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={usageData.dailyActivity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sessions" fill="#4F46E5" name="Sessions" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="chart-container">
                <h3>Questions Per Day</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={usageData.dailyActivity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="questions" fill="#F59E0B" name="Questions" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="activity-metrics">
              <h3>Usage Patterns</h3>
              <table className="metrics-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Sessions</th>
                    <th>Users</th>
                    <th>Questions</th>
                    <th>Avg. Questions/Session</th>
                  </tr>
                </thead>
                <tbody>
                  {usageData.dailyActivity?.map((day) => (
                    <tr key={day.day}>
                      <td>{day.day}</td>
                      <td>{day.sessions}</td>
                      <td>{day.users}</td>
                      <td>{day.questions}</td>
                      <td>{(day.questions / day.sessions).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {activeTab === 'learning-styles' && (
          <div className="learning-styles-tab">
            <div className="chart-row">
              <div className="chart-container">
                <h3>Learning Style Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={usageData.learningStyles}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {usageData.learningStyles?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} students`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="chart-container">
                <h3>Performance by Learning Style</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={usageData.performanceByStyle}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="correct" fill="#10B981" name="Correct %" />
                    <Bar dataKey="engagement" fill="#8B5CF6" name="Engagement %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="adaptation-metrics">
              <h3>Learning Style Adaptation</h3>
              <p>
                This model adapts to each student's learning style, adjusting its approach to match their preferences.
                The chart above shows performance across different learning styles.
              </p>
              
              <div className="style-recommendations">
                <h4>Recommendations</h4>
                <ul>
                  <li>
                    <strong>Visual learners</strong> are performing well with this model.
                    Consider adding more diagrams and graphical representations.
                  </li>
                  <li>
                    <strong>Auditory learners</strong> could benefit from more structured explanations.
                    Consider adding more verbal explanation patterns.
                  </li>
                  <li>
                    <strong>Kinesthetic learners</strong> show high engagement but lower correctness.
                    Consider adding more interactive examples.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'content' && (
          <div className="content-tab">
            <div className="chart-container">
              <h3>Most Common Topics</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={usageData.topics} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#4F46E5" name="Questions" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="content-analysis">
              <h3>Content Analysis</h3>
              <div className="cards-container">
                <div className="analysis-card">
                  <h4>Strengths</h4>
                  <ul>
                    <li>Strong performance in Equation solving (92% success)</li>
                    <li>Word Problems show good engagement (88%)</li>
                    <li>Visual learners find Function examples helpful</li>
                  </ul>
                </div>
                
                <div className="analysis-card">
                  <h4>Areas for Improvement</h4>
                  <ul>
                    <li>Polynomials content needs more examples</li>
                    <li>Inequalities topic needs clearer explanations</li>
                    <li>Add more kinesthetic approaches to Functions</li>
                  </ul>
                </div>
                
                <div className="analysis-card">
                  <h4>Content Recommendations</h4>
                  <ul>
                    <li>Add more visual examples for Polynomials</li>
                    <li>Create step-by-step guides for Inequalities</li>
                    <li>Develop interactive examples for Functions</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'feedback' && (
          <div className="feedback-tab">
            <div className="chart-container">
              <h3>Student Feedback Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      {name: 'Positive', value: usageData.studentFeedback?.positive, color: '#10B981'},
                      {name: 'Neutral', value: usageData.studentFeedback?.neutral, color: '#F59E0B'},
                      {name: 'Negative', value: usageData.studentFeedback?.negative, color: '#EF4444'}
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      {name: 'Positive', value: usageData.studentFeedback?.positive, color: '#10B981'},
                      {name: 'Neutral', value: usageData.studentFeedback?.neutral, color: '#F59E0B'},
                      {name: 'Negative', value: usageData.studentFeedback?.negative, color: '#EF4444'}
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="feedback-list">
              <h3>Recent Feedback</h3>
              <div className="feedback-cards">
                {usageData.studentFeedback?.recentFeedback.map((feedback, index) => (
                  <div 
                    key={index} 
                    className={`feedback-card ${feedback.type}`}
                  >
                    <p>{feedback.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LMSMonitoring;